<template>
  <div class="buyer-form" v-if="hasFields">
    <div v-for="(field, key) in fields" :key="key">
      <component :is="field.component" v-model="field.value" v-bind="field.props" v-on="field.events" :disabled="hasDisabled(key)"/>
    </div>
  </div>
</template>

<script>
import { Get } from "@/stores/api/methods";
import formInput from "@/views/baseComponents/formInput.vue";
import formSelect from "./fields/buyerSelect.vue";

export default {
  name: "BuyerForm",

  props: {
    citizenships: {
      type: Array,
      default: () => [],
    },
    travelers: {
      type: Array,
      default: () => [],
    },
    type: {
      type: Number,
      required: true,
    },
    user: {
      type: Object,
      default: () => {},
    },
  },

  data() {
    return {
      fields: [],
    };
  },

  async mounted() {
    await this.getFields();
    this.setFormData();
  },

  computed: {
    hasFields() {
      return Object.keys(this.fields).length;
    },

    tourists() {
      const items = this.travelers.map((el, idx) => ({
        text: `${this.localizeList.touristNumber} №${idx + 1}`,
        value: idx,
        isShow: true,
      }));

      return this.type === 4 ? [...items, { text: this.localizeList.TouristNotSelected, value: -1, isShow: true }] : items;
    },

    isTourist() {
      return this.type === 2 || this.type === 4;
    },

    getTouristIndex() {
      if (!this.isTourist || !this.hasFields) return;
      return this.fields.Tourist?.value || 0;
    },

    isTouristChanged() {
      if (!this.isTourist || !isFinite(!this.getTouristIndex)) return;
      return [this.getTouristIndex, this.travelers];
    },
  },

  methods: {
    async getFields() {
      const { data } = await Get(
        this.$store.getters["getApiUrl"] +
          "GetDogovorCustomerFields" +
          `?customerType=${this.type}` +
          `&userName=${this.user?.UserId}`
      );

      const sorted = data.sort((a, b) => a.Priority - b.Priority);
      const documents = await this.getDocumentTypes()
      const dates = {
        from: "1900.01.01",
        to: new Intl.DateTimeFormat('ru').format(new Date()).split('.').reverse().join('.')
      }
      const origin = sorted.map((el) => {
        const { PropertyName: label, Value: value, MaxLength: maxLength } = el;
        switch (label) {
          case "Birthday": {
            const field = {
              component: formInput,
              value: value || "",
              props: {
                originLabel: label,
                label: this.localizeList[label],
                dateMode: true,
                dateFrom: dates.from,
                dateTo: dates.to,
              },
              events: {
                input: (v) => this.datesValidation(field, v),
              }
            };

            return field
          }
          case "ContractDate": {
            const field = {
              component: formInput,
              value: value || "",
              props: {
                originLabel: label,
                label: this.localizeList[label],
                dateMode: true,
                dateFrom: dates.from,
                dateTo: dates.to,
              },
              events: {
                input: (v) => this.datesValidation(field, v),
              }
            };

            return field
          }
          case "CountryKey": {
            const field = {
              component: formSelect,
              props: {
                originLabel: label,
                label: this.localizeList[label],
                items: this.citizenships,
              },
              value,
              events: {
                focus: () => this.$emit('get-countries', "citizenship"),
              }
            };

            return field
          }
          case "DocumentType": {
            return {
              component: formSelect,
              props: {
                originLabel: label,
                label: this.localizeList[label],
                items: documents,
                selectMode: true,
              },
              value,
            };
          }
          case "ContractPrice": {
            return {
              component: formInput,
              props: {
                originLabel: label,
                label: this.localizeList[label],
                onlyNumbers: true,
                maxLength
              }
            }
          }
          default: {
            return {
              component: formInput,
              props: {
                originLabel: label,
                label: this.localizeList[label],
                maxLength
              },
              value,
            };
          }
        }
      });

      const modified = origin.reduce((acc, el) => {
        const label = el.props.originLabel;
        acc[label] = el;
        return acc;
      }, {});

      const Tourist = {
        component: formSelect,
        props: {
          originLabel: "Tourist",
          label: this.localizeList.touristNumber,
          items: this.tourists,
          selectMode: true,
        },
        value: 0,
      };

      this.fields = this.isTourist ? { Tourist, ...modified } : modified;
    },

    datesValidation(obj, value) {
      let cleanValue = value.replace(/\D/g, '');
      if (cleanValue.length > 2) {
        cleanValue = cleanValue.slice(0, 2) + '.' + cleanValue.slice(2);
      }

      if (cleanValue.length > 5) {
        cleanValue = cleanValue.slice(0, 5) + '.' + cleanValue.slice(5, 9);
      }

      if (cleanValue.length > 10) {
        cleanValue = cleanValue.slice(0, 10);
      }

      this.$nextTick(() => obj.value = cleanValue)
    },

    async getDocumentTypes() {
      const { data } = await Get(
        this.$store.getters["getApiUrl"] + "GetCustomerDocumentTypes"
      );

      const result =  data.map((el) => {
        const { Value, Key } = el;
        return { text: this.localizeList[Key], value: Value, isShow: true };
      });

      result.push(result.shift());
      return result
    },

    setFormData() {
      if (!this.isTourist || !this.hasFields || this.getTouristIndex === -1) return;

      const traveler = this.travelers[this.getTouristIndex];
      this.setFullName(traveler);
      this.setPassportData(traveler);
      this.fields.Address.value = this.getValueFromItem(traveler, "residenceregion", this.fields.Address.value);
      this.fields.Phone.value = this.getValueFromItem(traveler, "phone", this.fields.Phone.value);
      this.fields.Email.value = this.getValueFromItem(traveler, "email", this.fields.Email.value);
      this.fields.CountryKey.value = this.getValueFromItem(traveler, "citizenship", this.fields.CountryKey.value);

      if(this.fields.Birthday) {
        this.fields.Birthday.value = this.getValueFromItem(traveler, "birthdate", this.fields.Birthday.value);
      }
    },

    setFullName(item) {
      const firstname = this.getValueFromItem(item, ["firstname", "firstnamerus"]);
      const lastname = this.getValueFromItem(item, ["lastname", "lastnamerus"]);
      const patronymic = this.getValueFromItem(item, ["patronymic", "patronymicrus"]);
      const fullname = [lastname, firstname, patronymic].filter((el) => el).join(" ");
      this.fields.FullName.value = fullname || this.fields.FullName.value;
    },

    setPassportData(item) {
      const passport = this.getValueFromItem(item, ["passport", "passportrus"]);
      const [number, series] = passport.split("/");
      this.fields.PassportSer.value = number || this.fields.PassportSer.value;
      this.fields.PassportNum.value = series || this.fields.PassportNum.value;
    },

    getValueFromItem(item, code, origin = "") {
      const list = Array.isArray(code) ? code : [code];
      const { value } = item.find((el) => list.some((item) => el.code === item)) ?? {};
      return value || origin;
    },

    clearFields() {
      Object.keys(this.fields).forEach(key => {
        if (key !== 'Tourist') {
          this.fields[key].value = '';
        }
      });
    },
    hasDisabled(key) {
      const hasUser = Object.keys(this.user).length
      return !hasUser && key !== 'Tourist'
    }
  },

  watch: {
    fields: {
      handler(value) {
        this.$emit("input", value);
      },
      deep: true,
      immediate: true,
    },

    async type() {
      this.fields = [];
      await this.getFields();
    },

    isTouristChanged() {
        this.setFormData();
    },

    citizenships(val) {
      this.fields.CountryKey.props.items = val
    }
  },

  components: {
    formInput,
    formSelect,
  },
};
</script>

<style scoped></style>